/*
 * Copyright (c) 2026 Bolmate B.V.
 *
 * List of active device tokens for the current user. Each row shows the
 * platform label + last_seen_at and lets the user revoke that specific
 * device — handy when you lose a phone.
 */

import React, {forwardRef, useContext, useEffect, useImperativeHandle, useLayoutEffect, useRef, useState} from 'react';

import {useTranslation} from 'react-i18next';

import {AwesomeIcon, ResponseData, showDialog, showSnackbar} from '@dvrd/dvr-controls';

import IDate from '@dvrd/idate';

import IconWithLabel from '../icon/iconWithLabel';

import BolmateDialog from '../controls/dialog/bolmateDialog';

import PushGuardHint from './pushGuardHint';

import {deleteDeviceToken, listDeviceTokens, renameDeviceToken, sendTestPushToDevice} from '../../util/requestSender';

import {getOptedIn} from '../../hooks/usePushSubscription';

import {getLastEndpoint} from '../../util/pushIdb';

import {onShowConfirmSnackbar} from '../../util/renderUtil';

import {DUTCH_DATE_FORMAT} from '../../util/constants';

import {DeviceListHandle} from '../../util/interfaces';

import {AppContext} from '../misc/appContext';

export type {DeviceListHandle};

interface DeviceListProps {
    /** Fired after a device is removed, so PushNotificationsCard can re-read
     *  browser state and flip its toggle when the current device was logged
     *  out. */
    onDeviceRemoved?: () => void;
}

interface DeviceToken {
    id: string;
    platform: string | null;
    /** User-chosen nickname. When set it replaces the auto `platform` label as
     *  the row's primary name (the auto label still shows as a subtitle), so
     *  two same-OS/browser devices are distinguishable. null/blank = no
     *  nickname, fall back to the auto label. */
    custom_name?: string | null;
    user_agent?: string | null;
    /** Immutable first-registration time (kept for support); NOT the "Added:"
     *  label — a re-enable does not move it. */
    created_at?: string | null;
    /** When push was last (re-)enabled on this device — what the "Added:" label
     *  shows. A re-enable moves it forward; created_at stays put. */
    activated_at?: string | null;
    /** Liveness timestamp; bumped on every re-register / delivery confirm, so it
     *  must NOT drive the "Added:" label (it would appear to keep updating). */
    last_seen_at?: string | null;
    channel?: string;
    endpoint?: string | null;
}

// Pick a device-type icon from the STORED user_agent (language-independent —
// device.platform is localized, so we don't key on it). Falls back to the label
// only when no user_agent is stored. 'desktop' / 'mobile' / 'tablet' are all
// long-standing FontAwesome names.
function deviceTypeIcon(device: DeviceToken): 'desktop' | 'mobile' | 'tablet' {
    const ua = device.user_agent || '';
    if (/iPad/.test(ua) || (/Android/.test(ua) && !/Mobile/.test(ua))) return 'tablet';
    if (/iPhone|iPod|Mobile|webOS|BlackBerry|IEMobile/.test(ua)) return 'mobile';
    if (!ua) {
        const label = device.platform || '';
        if (/tablet/i.test(label)) return 'tablet';
        if (/mobile|phone|telefoon|mobiel/i.test(label)) return 'mobile';
    }
    return 'desktop';
}

// Drop the "<device type> – " prefix from the stored label — now redundant since
// the type icon conveys it. Format is "Computer – macOS 26, Chrome"; we keep
// everything after the FIRST " – " (or " - ") separator. Locale-independent
// (splits on the separator, not the localized type words); a prefix-less label
// or a raw user_agent is returned unchanged.
function stripDeviceTypePrefix(label: string): string {
    const match = label.match(/^.+? [–-] (.+)$/);
    return match ? match[1] : label;
}

// Human OS name for the row (iOS / iPadOS / Android / Windows / macOS / …),
// derived from the STORED user_agent (language-independent). Falls back to the
// platform label for older rows that stored no UA, and for iPadOS 13+ Safari,
// which reports a desktop "Macintosh" UA — the row's label ("iPad, …", set at
// registration via maxTouchPoints) is the only signal left, so the label is
// checked for iPad before the UA falls through to macOS. Returns '' when the OS
// cannot be determined.
function deviceOSName(device: DeviceToken): string {
    const ua = device.user_agent || '';
    const label = device.platform || '';
    if (/iPhone|iPod/.test(ua) || /iPhone|iPod/i.test(label)) return 'iOS';
    if (/iPad/.test(ua) || /iPad/i.test(label)) return 'iPadOS';
    if (/Android/.test(ua) || /Android/i.test(label)) return 'Android';
    if (/Windows/.test(ua) || /Windows/i.test(label)) return 'Windows';
    if (/CrOS/.test(ua)) return 'ChromeOS';
    if (/Macintosh|Mac OS X/.test(ua) || /mac ?os/i.test(label)) return 'macOS';
    if (/Linux/.test(ua)) return 'Linux';
    return '';
}

// The list caps its visible height to this many rows; a longer list scrolls
// (backend caps the total at 10, so this is the "see 5 at a glance" window).
const MAX_VISIBLE_DEVICES = 5;

const DeviceList = forwardRef<DeviceListHandle, DeviceListProps>((props, ref) => {
    const {t} = useTranslation();
    const {onDeviceRemoved} = props;
    const context = useContext(AppContext);
    // Reload on (user, account) change. Multi-user invariant preserved
    // (JWT switch on admin impersonation) + per-account scoping so the
    // device list shows ONLY devices opted in for THIS sales account.
    const userId = context.userContext.user?.id ?? null;
    const accountId = context.accountContext.account?.id ?? null;
    const [devices, setDevices] = useState<Array<DeviceToken>>([]);
    const [currentEndpoint, setCurrentEndpoint] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    // Inline nickname editing: the id of the row being edited (null = none) and
    // the draft text. `savingRename` blocks a double-submit while the PATCH is
    // in flight.
    const [editingId, setEditingId] = useState<string | null>(null);
    const [editValue, setEditValue] = useState('');
    const [savingRename, setSavingRename] = useState(false);
    // Id of the device whose per-device test push is in flight (spins its icon,
    // blocks a double-send). null = none sending.
    // Pure re-entrancy guard — since the spinner on the row is gone it no
    // longer affects the render, so a ref avoids two useless re-renders per
    // test send.
    const testingDeviceId = useRef<string | null>(null);
    const renameInputRef = useRef<HTMLInputElement>(null);
    // Height cap for the list: measured from the first MAX_VISIBLE_DEVICES rows
    // (rows vary in height — nickname subtitle, mobile wrap), undefined = no cap.
    const listRef = useRef<HTMLUListElement>(null);
    const [listMaxHeight, setListMaxHeight] = useState<number>();
    // Monotonic id of the newest load(); in-flight callbacks from an older
    // load (delayed by the awaited orphan cleanup) compare against it and
    // drop their result instead of overwriting the newer account's list.
    const loadSeqRef = useRef(0);

    // Sync the list with the BROWSER PERMISSION. When the user reset/revoked the
    // site's notification permission, this device has NO live subscription, yet
    // the backend still lists THIS device's token (registered before the reset).
    // It would only disappear after the next send 410s it. Find that orphan —
    // matched by this device's last-known endpoint — and unregister it so the
    // list reflects the browser immediately. Returns the removed token id (so it
    // can be dropped from the displayed rows at once), or null. Tightly gated:
    // only when permission is NOT 'granted' AND there is genuinely no live
    // subscription here, so a working, granted context is never touched.
    async function deleteOrphanToken(rows: Array<DeviceToken>): Promise<string | null> {
        if (!accountId || !userId) return null;
        if (typeof Notification === 'undefined' || Notification.permission === 'granted') return null;
        let lastEndpoint: string | null = null;
        try {
            const reg = await navigator.serviceWorker?.getRegistration('/');
            const sub = (await reg?.pushManager.getSubscription()) ?? null;
            if (sub) return null; // a live subscription exists here → not orphaned
            lastEndpoint = await getLastEndpoint(userId);
        } catch {
            return null;
        }
        if (!lastEndpoint) return null;
        const orphan = rows.find(r => r.endpoint === lastEndpoint);
        if (!orphan) return null;
        // Await the backend DELETE and only report the row as removed when it
        // SUCCEEDED — the previous fire-and-forget hid the row immediately,
        // so a failed delete left the token alive on the backend and the
        // "removed" device popped back on the next refresh.
        const deleted = await new Promise<boolean>(resolve => {
            deleteDeviceToken({
                token_id: orphan.id,
                account_id: accountId,
                callback: (data: ResponseData) => resolve(!!data.success),
                errorCallback: () => resolve(false)
            });
        });
        if (!deleted) return null;
        onDeviceRemoved?.();
        return orphan.id;
    }

    function load() {
        if (!accountId) {
            setDevices([]);
            setLoading(false);
            return;
        }
        setLoading(true);
        const seq = ++loadSeqRef.current;
        listDeviceTokens({
            account_id: accountId,
            callback: async (data: ResponseData) => {
                if (seq !== loadSeqRef.current) return; // superseded by a newer load
                if (!data.success) {
                    setLoading(false);
                    showSnackbar(t('push.error', 'Er ging iets mis, probeer het opnieuw'), {
                        backgroundColor: 'color-blue-bolmate',
                        textColor: 'white'
                    });
                    return;
                }
                const rows: Array<DeviceToken> = data.tokens || data.device_tokens || [];
                const orphanId = await deleteOrphanToken(rows);
                if (seq !== loadSeqRef.current) return; // account switched mid-await
                setDevices(orphanId ? rows.filter(r => r.id !== orphanId) : rows);
                setLoading(false);
            },
            // Network failure / non-2xx never fires `callback` — drop the
            // spinner instead of showing "Laden..." forever.
            errorCallback: () => {
                if (seq !== loadSeqRef.current) return;
                setLoading(false);
            }
        });
        if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
            navigator.serviceWorker.ready
                .then(reg => reg.pushManager.getSubscription())
                .then(sub => setCurrentEndpoint(sub?.endpoint ?? null))
                .catch(() => setCurrentEndpoint(null));
        }
    }

    // Deps must include (userId, accountId) — `load` captures accountId in
    // its closure. With [] deps the imperative handle would freeze the
    // first-render `load` (often with accountId=null because AppContext
    // hadn't populated yet), so parent-triggered refresh would fetch the
    // wrong account or no-op entirely.
    useImperativeHandle(ref, () => ({refresh: load}), [userId, accountId]);

    useEffect(() => {
        // Reset to loading state immediately so the stale previous list is
        // not visible during a (user, account) transition. Reload happens
        // whenever EITHER user or sales account changes.
        setDevices([]);
        setLoading(true);
        load();
    }, [userId, accountId]);

    function formatLastSeen(raw: string | null | undefined): string {
        if (!raw) return '-';
        // Force 24h: IDate's "HH" token maps to hour:'2-digit' but doesn't pin the
        // hour cycle, so it follows the browser locale (12h on some devices). Build
        // the time manually from the local hour/minute to guarantee 24h.
        const date = new IDate(raw);
        const hh = String(date.hours()).padStart(2, '0');
        const mm = String(date.minutes()).padStart(2, '0');
        return `${date.format(DUTCH_DATE_FORMAT)} ${hh}:${mm}`;
    }

    // The auto-detected label ("Computer – macOS 26, Chrome" → "macOS 26,
    // Chrome"). Shown as the primary name when there is no nickname, and as a
    // subtitle when there is (so the technical detail is never lost).
    function deviceAutoLabelText(device: DeviceToken): string {
        const stripped = stripDeviceTypePrefix(device.platform || device.user_agent || '');
        // Older rows stored the opaque "(PWA)" — show the friendly localized term.
        const label = stripped.replace('(PWA)', `(${t('push.installedApp', 'geïnstalleerde app')})`);
        return label || t('push.unknownDevice', 'Onbekend apparaat');
    }

    // The row's primary name: the user's nickname when set, else the auto label.
    function deviceLabelText(device: DeviceToken): string {
        const custom = (device.custom_name || '').trim();
        return custom || deviceAutoLabelText(device);
    }

    function onConfirmRemove(device: DeviceToken, isCurrentDevice: boolean) {
        if (!accountId) return;
        deleteDeviceToken({
            token_id: device.id,
            account_id: accountId,
            callback: async (data: ResponseData) => {
                if (data.success) {
                    if (isCurrentDevice) {
                        // Multi-user invariant + per-account: only clear
                        // the current (user, account) opt-in flag. Do NOT
                        // touch the browser subscription — other (user,
                        // account) pairs on this device may still want it.
                        if (userId) {
                            try {
                                localStorage.removeItem('bolmate-push-opted-in:' + userId + ':' + accountId);
                            } catch (_e) {
                                /* */
                            }
                        }
                    }
                    onShowConfirmSnackbar(t('push.logoutDeviceDone', 'Apparaat is uitgelogd'));
                    load();
                    onDeviceRemoved?.();
                } else {
                    showSnackbar(t('push.error', 'Er ging iets mis, probeer het opnieuw'), {
                        backgroundColor: 'color-blue-bolmate',
                        textColor: 'white'
                    });
                }
            },
            // `callback` fires only on 2xx — without this a network failure /
            // non-2xx removes nothing and shows nothing: the dialog closed, the
            // row stayed, and the user gets no explanation. Surface the error.
            errorCallback: () => {
                showSnackbar(t('push.error', 'Er ging iets mis, probeer het opnieuw'), {
                    backgroundColor: 'color-blue-bolmate',
                    textColor: 'white'
                });
            }
        });
    }

    function onRemove(device: DeviceToken) {
        const isCurrentDevice = !!(currentEndpoint && device.endpoint && device.endpoint === currentEndpoint);
        showDialog(
            t('push.logoutDeviceConfirm', 'Weet je zeker dat je dit apparaat wilt uitloggen?'),
            t('push.logoutDevice', 'Apparaat uitloggen'),
            [
                t('common.no', 'Nee'),
                {
                    label: t('common.yes', 'Ja'),
                    primary: false,
                    onClick: () => onConfirmRemove(device, isCurrentDevice)
                }
            ]
        );
    }

    // Send a test push to THIS device only (the row's paper-plane).
    function onSendTestDevice(device: DeviceToken) {
        if (!accountId || testingDeviceId.current) return;
        testingDeviceId.current = device.id;
        sendTestPushToDevice({
            token_id: device.id,
            account_id: accountId,
            callback: (data: ResponseData) => {
                testingDeviceId.current = null;
                if (data.success) {
                    onShowConfirmSnackbar(t('push.testSentDevice', 'Testmelding naar dit apparaat verzonden'));
                } else {
                    const code = (data as {error_code?: string}).error_code;
                    const fallback =
                        (data as {message?: string}).message ||
                        t('push.error', 'Er ging iets mis, probeer het opnieuw');
                    const msg = code ? t(`push.testErrors.${code}`, fallback) : fallback;
                    showSnackbar(msg, {backgroundColor: 'color-blue-bolmate', textColor: 'white'});
                }
            },
            // `callback` fires only on 2xx — on a network failure / non-2xx the
            // re-entrancy ref would latch forever (no spinner, no feedback), so
            // every test button on the panel goes dead until remount. Clear it
            // and surface the error.
            errorCallback: () => {
                testingDeviceId.current = null;
                showSnackbar(t('push.error', 'Er ging iets mis, probeer het opnieuw'), {
                    backgroundColor: 'color-blue-bolmate',
                    textColor: 'white'
                });
            }
        });
    }

    // Open the rename dialog for a device, prefilled with the name currently
    // shown (the nickname if set, else the auto-detected label) so the user
    // tweaks the existing text instead of typing the whole name from scratch.
    function startEdit(device: DeviceToken) {
        setEditingId(device.id);
        setEditValue(deviceLabelText(device));
    }

    function cancelEdit() {
        if (savingRename) return; // don't drop the dialog mid-save
        setEditingId(null);
        setEditValue('');
    }

    function saveEdit() {
        const device = devices.find(d => d.id === editingId);
        if (!device || !accountId || savingRename) return;
        const name = editValue.trim();
        setSavingRename(true);
        renameDeviceToken({
            token_id: device.id,
            account_id: accountId,
            // Blank clears the nickname — the row falls back to its auto label.
            custom_name: name || null,
            callback: (data: ResponseData) => {
                setSavingRename(false);
                if (data.success) {
                    // Patch the row in place instead of a full reload — keeps the
                    // list from flashing and preserves scroll position.
                    setDevices(prev => prev.map(d => (d.id === device.id ? {...d, custom_name: name || null} : d)));
                    setEditingId(null);
                    setEditValue('');
                } else {
                    showSnackbar(t('push.error', 'Er ging iets mis, probeer het opnieuw'), {
                        backgroundColor: 'color-blue-bolmate',
                        textColor: 'white'
                    });
                }
            },
            // `callback` only fires on 2xx — without this a failed PATCH (offline,
            // 5xx) would leave savingRename stuck true, freezing the dialog in
            // loading with cancelEdit disabled (only a page reload escapes it).
            errorCallback: () => {
                setSavingRename(false);
                showSnackbar(t('push.error', 'Er ging iets mis, probeer het opnieuw'), {
                    backgroundColor: 'color-blue-bolmate',
                    textColor: 'white'
                });
            }
        });
    }

    // Focus the rename input when the dialog opens, cursor AT THE END (not
    // select-all — that would wipe the prefilled name on the first keystroke).
    // The prefill stays so the user edits the existing name instead of retyping.
    useEffect(() => {
        if (editingId === null) return;
        const id = window.setTimeout(() => {
            const input = renameInputRef.current;
            if (!input) return;
            input.focus();
            const end = input.value.length;
            input.setSelectionRange(end, end);
        }, 0);
        return () => window.clearTimeout(id);
    }, [editingId]);

    // Hide ONLY the current browser's device row when this browser is not
    // opted in for the current (user, account) pair. The localStorage opt-in
    // flag is per-browser, but the backend stores rows per-(user, account,
    // endpoint) — so other browsers' rows (e.g. user's desktop while viewing
    // from phone) are legitimate even when this browser is opted out. Hiding
    // them too would falsely suggest the user has no push devices anywhere
    // for this account, when in fact other devices are still registered.
    //
    // The narrow filter still solves the original race: when localStorage
    // says OFF but backend still has a row for THIS endpoint (phantom from
    // a previous session or pre-account migration), it's hidden until
    // refresh()'s phantom cleanup deletes it.
    const optedIn = getOptedIn(userId, accountId);
    const isThisDevice = (d: DeviceToken) => !!(currentEndpoint && d.endpoint && d.endpoint === currentEndpoint);
    const visibleDevices = devices
        .filter(d => {
            if (!optedIn && currentEndpoint && d.endpoint === currentEndpoint) return false;
            return true;
        })
        // "(this device)" always on top; the rest newest-added first (by the same
        // activated_at the "Added:" label shows, created_at fallback). slice()
        // first so we sort a copy, never the `devices` state array.
        .slice()
        .sort((a, b) => {
            const aThis = isThisDevice(a);
            const bThis = isThisDevice(b);
            if (aThis !== bThis) return aThis ? -1 : 1;
            const at = a.activated_at ?? a.created_at;
            const bt = b.activated_at ?? b.created_at;
            return (bt ? Date.parse(bt) : 0) - (at ? Date.parse(at) : 0);
        });
    // Only show the loading placeholder on the FIRST load (nothing to show yet).
    // On a refresh (enable/disable, manual refresh, visibility) we keep the
    // current list mounted — swapping it for the small "Laden..." text and back
    // is what made the whole view jump. A subtle dimming signals the refresh.
    const isInitialLoad = loading && devices.length === 0;

    // Cap the list to the first MAX_VISIBLE_DEVICES rows so a long list scrolls
    // instead of pushing the rest of the card off-screen. Measured (not a fixed
    // rem) because rows differ in height — a nickname adds a subtitle line, the
    // mobile layout wraps. useLayoutEffect applies the cap before paint (no
    // flash of the full list); the ResizeObserver re-measures on width changes.
    useLayoutEffect(() => {
        const ul = listRef.current;
        if (!ul) return;
        const measure = () => {
            const rows = Array.from(ul.children) as Array<HTMLElement>;
            if (rows.length <= MAX_VISIBLE_DEVICES) {
                setListMaxHeight(undefined);
                return;
            }
            let h = 0;
            for (let i = 0; i < MAX_VISIBLE_DEVICES; i++) h += rows[i].offsetHeight;
            setListMaxHeight(h);
        };
        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(ul);
        return () => observer.disconnect();
    }, [visibleDevices]);

    return (
        <div className='push-devices'>
            <div className='push-devices-header'>
                <div className='push-devices-title'>{t('push.devices', 'Apparaten')}</div>
                {/* The test-push-to-all-devices action moved to the push card's
                    header (PushNotificationsCard) — it is an account-wide push
                    test, not a per-row device action. */}
            </div>
            {/* The push guard hint (why push can't work here) lives at the top of
                this section, under the header — moved out of the card body. */}
            <PushGuardHint />
            {isInitialLoad ? (
                <div className='push-devices-empty'>{t('push.loading', 'Laden...')}</div>
            ) : visibleDevices.length === 0 ? (
                <div className='push-devices-empty'>{t('push.devicesNone', 'Geen apparaten geregistreerd')}</div>
            ) : (
                <ul
                    ref={listRef}
                    className={'push-devices-list' + (loading ? ' is-refreshing' : '')}
                    style={listMaxHeight !== undefined ? {maxHeight: listMaxHeight, overflowY: 'auto'} : undefined}
                >
                    {visibleDevices.map(device => {
                        const isCurrent = !!(currentEndpoint && device.endpoint && device.endpoint === currentEndpoint);
                        const hasNickname = !!(device.custom_name || '').trim();
                        const osName = deviceOSName(device);
                        return (
                            <li key={device.id} className='push-devices-row'>
                                <div className='push-devices-meta'>
                                    <span className='push-devices-platform'>
                                        <AwesomeIcon name={deviceTypeIcon(device)} className='push-devices-type-icon' />
                                        {osName && <span className='push-devices-os'>{osName}</span>}
                                        {/* Name truncates; the "(this device)" marker is a
                                            sibling with flex-shrink:0 so a very long name can
                                            never push it out of view. */}
                                        <span className='push-devices-platform-text'>{deviceLabelText(device)}</span>
                                        {isCurrent && (
                                            <span className='push-devices-current'>
                                                {t('push.thisDevice', '(dit apparaat)')}
                                            </span>
                                        )}
                                    </span>
                                    {hasNickname && (
                                        <span className='push-devices-last-seen'>{deviceAutoLabelText(device)}</span>
                                    )}
                                    <span className='push-devices-last-seen'>
                                        {t('push.deviceAdded', 'Toegevoegd:')}{' '}
                                        {formatLastSeen(
                                            device.activated_at ?? device.created_at ?? device.last_seen_at
                                        )}
                                    </span>
                                    <span className='push-devices-last-seen'>
                                        {t('push.deviceLastSeen', 'Laatst gezien:')}{' '}
                                        {formatLastSeen(device.last_seen_at)}
                                    </span>
                                </div>
                                <div className='push-devices-actions'>
                                    <IconWithLabel
                                        label={t('push.editName', 'Naam wijzigen')}
                                        name='pen'
                                        onClick={() => startEdit(device)}
                                        iconClassName='action btn-edit'
                                    />
                                    <IconWithLabel
                                        label={t('push.testSendDevice', 'Testmelding naar dit apparaat sturen')}
                                        name='paper-plane'
                                        onClick={() => onSendTestDevice(device)}
                                        iconClassName='action btn-test'
                                    />
                                    <IconWithLabel
                                        label={t('push.logoutDevice', 'Apparaat uitloggen')}
                                        name='trash-alt'
                                        onClick={() => onRemove(device)}
                                        iconClassName='action btn-delete'
                                    />
                                </div>
                            </li>
                        );
                    })}
                </ul>
            )}
            <BolmateDialog
                active={editingId !== null}
                onClose={cancelEdit}
                onConfirm={saveEdit}
                title={t('push.editName', 'Naam wijzigen')}
                confirmLabel={t('push.save', 'Opslaan')}
                cancelLabel={t('push.cancel', 'Annuleren')}
                loading={savingRename}
                width='30rem'
            >
                <div className='push-rename-body'>
                    <label className='push-rename-label' htmlFor='push-rename-input'>
                        {t('push.editNameHint', 'Geef dit apparaat een herkenbare naam')}
                    </label>
                    <input
                        id='push-rename-input'
                        ref={renameInputRef}
                        className='push-rename-input'
                        value={editValue}
                        maxLength={100}
                        disabled={savingRename}
                        placeholder={t('push.namePlaceholder', 'Bijv. MacBook werk')}
                        aria-label={t('push.editName', 'Naam wijzigen')}
                        onChange={e => setEditValue(e.target.value)}
                        onKeyDown={e => {
                            if (e.key === 'Enter') saveEdit();
                        }}
                    />
                </div>
            </BolmateDialog>
        </div>
    );
});

DeviceList.displayName = 'DeviceList';

export default DeviceList;
